/**
 * Front-end JavaScript errors, sent to GA4 as `qq1_js_error` through Zaraz.
 *
 * Microsoft Clarity counts script errors but its export has no messages
 * (19 Oct 2026: 44 errors in about 26 of ~4,100 sessions over 3 days).
 * This event carries a short, scrubbed message plus where it came from, so
 * the errors can be read in GA4. The "^qq1_" trigger in Zaraz forwards it.
 *
 * Privacy: the message is cut to 100 characters, and emails, phone numbers
 * and any run of 5+ digits are replaced. No stack trace, no query strings,
 * and nothing read from form fields.
 *
 * Volume: one event per distinct error per page view, at most 5 per page
 * view. A client-side route change to a new path starts a new page view.
 *
 * Noise: errors thrown by browser extensions and by in-app browsers' own
 * bridge code are not sent (19-23 Oct 2026: "runtime.sendMessage(). Tab not
 * found." and "window.webkit.messageHandlers", none of it ours). The rest
 * carry `error_code`, a class of where the error came from (see
 * classifyOrigin), so a report can keep only "app" and "inline".
 *
 * Zaraz forwards only reason, field, error_code, ms and attempt to GA4 (its
 * "QQ1 diagnostics" action lists them), so those carry what a report needs.
 *
 * Stack overflows ("Maximum call stack size exceeded", 22 and 26 Oct 2026):
 * the throw site alone does not say what looped, so for these `field` is
 * the throw site with its column plus the top 3 stack frames, each only
 * file:line:column (see stackField). Other errors keep source:line.
 */
import { qqTrack } from "@/lib/qq-diagnostics";

export const JS_ERROR_EVENT = "qq1_js_error";
const MAX_PER_PAGE_VIEW = 5;
const MAX_MESSAGE_LENGTH = 100;
// Zaraz loads async; hold early errors this long waiting for zaraz.track.
const ZARAZ_WAIT_MS = 20_000;

type Origin = "app" | "inline" | "cdn_cgi" | "third_party" | "opaque" | "unknown";

type ErrorProps = {
  message: string;
  source: string;
  line: string;
  column: string;
  page_path: string;
  party: "first_party" | "third_party" | "unknown";
  kind: "error" | "unhandledrejection";
  // Copies of the fields above under parameter names GA4 already has as
  // custom dimensions, so errors can be read before message/source/party/
  // kind are registered (and in the history from before then).
  reason: string;
  field: string;
  event_category: string;
  event_label: string;
  error_code: Origin;
};

/** Replaces emails, phone numbers and 5+ digit runs, then truncates. */
export function scrubMessage(raw: unknown): string {
  let msg = typeof raw === "string" ? raw : String(raw ?? "");
  msg = msg
    .replace(/[^\s@"'<>()]+@[^\s@"'<>()]+\.[a-z]{2,}/gi, "[email]")
    .replace(/\+?\(?\d[\d\s().-]{8,}\d/g, "[phone]")
    .replace(/\d{5,}/g, "[num]")
    .replace(/\s+/g, " ")
    .trim();
  if (msg.length > MAX_MESSAGE_LENGTH) msg = msg.slice(0, MAX_MESSAGE_LENGTH - 1) + "…";
  return msg || "(no message)";
}

/** hostname + path of a script URL, no query string or hash. */
export function describeSource(url: string | undefined): {
  source: string;
  party: ErrorProps["party"];
} {
  if (!url) return { source: "", party: "unknown" };
  try {
    const u = new URL(url, window.location.href);
    if (u.protocol !== "http:" && u.protocol !== "https:") {
      return { source: u.protocol.replace(":", ""), party: "unknown" };
    }
    const party = u.hostname === window.location.hostname ? "first_party" : "third_party";
    return { source: scrubMessage(u.hostname + u.pathname), party };
  } catch {
    return { source: "", party: "unknown" };
  }
}

// Extension code, by URL scheme. Safari reports extension scripts as
// webkit-masked-url://hidden/.
const EXTENSION_URL =
  /\b(?:chrome|moz|ms-browser|safari(?:-web)?)-extension:\/\/|\bwebkit-masked-url:\/\//;
// Messages only extensions and in-app browsers' injected bridges throw.
const BROWSER_NOISE = [
  /runtime\.sendMessage/,
  /Extension context invalidated/,
  /webkit\.messageHandlers/,
  /__gCrWeb/,
];

/**
 * True when the error comes from a browser extension or an in-app browser's
 * injected bridge, judged by the message, the script URL or the top stack
 * frame. Only the top frame: our own call through an extension's wrapper
 * (of fetch, say) still counts as ours.
 */
export function isBrowserNoise(message: string, url: string | undefined, stack: unknown): boolean {
  if (url && EXTENSION_URL.test(url)) return true;
  const top = firstFrame(stack).url;
  if (top && EXTENSION_URL.test(top)) return true;
  return BROWSER_NOISE.some((re) => re.test(message));
}

/**
 * Where an error came from, sent as `error_code`:
 * app (our /assets/ bundle), inline (a script in the page's HTML: ours, or
 * one an extension or in-app browser injected), cdn_cgi (Zaraz and other
 * Cloudflare scripts), third_party (another host), opaque ("Script error.",
 * a cross-origin script whose details the browser hides), unknown.
 */
export function classifyOrigin(message: string, url: string | undefined): Origin {
  if (!url) return /^Script error\.?$/i.test(message) ? "opaque" : "unknown";
  try {
    const u = new URL(url, window.location.href);
    if (u.protocol !== "http:" && u.protocol !== "https:") return "unknown";
    if (u.hostname !== window.location.hostname) return "third_party";
    if (u.pathname.startsWith("/assets/")) return "app";
    if (u.pathname.startsWith("/cdn-cgi/")) return "cdn_cgi";
    return "inline";
  } catch {
    return "unknown";
  }
}

const DEEP_STACK = /call stack/i;
// GA4 drops event parameter values over 100 characters.
const MAX_FIELD_LENGTH = 100;
const FRAME_URL = /([a-z][a-z-]*:\/\/[^\s)]+?):(\d+):(\d+)/;

/** Last path segment of a URL, without host, query or hash: "index-abc.js", "restore-west". */
function baseName(url: string): string {
  const path = url.replace(/^[a-z][a-z-]*:\/\/[^/]*/i, "").replace(/[?#].*$/, "");
  const parts = path.split("/").filter(Boolean);
  return scrubMessage(parts.length ? parts[parts.length - 1] : "(root)");
}

/**
 * The top `max` frames of a stack as "file:line:col". Function names, hosts,
 * paths, query strings and argument values are dropped. Frames without a URL
 * are "anonymous" (a script added without a src), "native" or "eval"; lines
 * that are not frames (the message) are skipped.
 */
export function stackFrames(stack: unknown, max = 3): string[] {
  if (typeof stack !== "string") return [];
  const out: string[] = [];
  for (const raw of stack.split("\n")) {
    if (out.length >= max) break;
    const line = raw.trim();
    if (!line) continue;
    const m = line.match(FRAME_URL);
    const anon = m ? null : line.match(/<anonymous>:(\d+):(\d+)/);
    if (m) out.push(`${baseName(m[1])}:${m[2]}:${m[3]}`);
    // A script added without a src (Chrome names it <anonymous>).
    else if (anon) out.push(`anonymous:${anon[1]}:${anon[2]}`);
    else if (/\[native code\]/.test(line)) out.push("native");
    else if (/^(?:at )?eval\b|eval code/.test(line)) out.push("eval");
  }
  return out;
}

/**
 * `field` for a stack overflow: "restore-west:192:15 | a:1:2 < b:3:4 < native",
 * cut at a whole frame to stay within GA4's 100 characters.
 */
export function stackField(
  url: string | undefined,
  line?: number,
  column?: number,
  stack?: unknown,
): string {
  let out = `${url ? baseName(url) : "(no file)"}:${line ?? ""}:${column ?? ""}`;
  const frames = stackFrames(stack);
  if (frames.length) {
    let joined = "";
    for (const f of frames) {
      const next = joined ? `${joined} < ${f}` : f;
      if (`${out} | ${next}`.length > MAX_FIELD_LENGTH) break;
      joined = next;
    }
    if (joined) out += ` | ${joined}`;
  }
  return out.slice(0, MAX_FIELD_LENGTH);
}

/** First `url:line:col` frame in a stack, for rejections that lack one. */
export function firstFrame(stack: unknown): { url?: string; line?: number; column?: number } {
  if (typeof stack !== "string") return {};
  const m = stack.match(/([a-z][a-z-]*:\/\/[^\s)]+?):(\d+):(\d+)/);
  return m ? { url: m[1], line: Number(m[2]), column: Number(m[3]) } : {};
}

let installed = false;

export function initJsErrorTracking(): void {
  if (typeof window === "undefined" || installed) return;
  installed = true;

  let pagePath = window.location.pathname;
  let seen = new Set<string>();
  let sent = 0;
  const queue: ErrorProps[] = [];
  let sending = false;

  const zarazReady = () =>
    typeof (window as unknown as { zaraz?: { track?: unknown } }).zaraz?.track === "function";

  const flush = () => {
    if (!zarazReady()) return;
    while (queue.length) {
      const props = queue.shift()!;
      sending = true;
      try {
        qqTrack(JS_ERROR_EVENT, props);
      } finally {
        sending = false;
      }
    }
  };

  const report = (
    kind: ErrorProps["kind"],
    rawMessage: unknown,
    url?: string,
    line?: number,
    column?: number,
    stack?: unknown,
  ) => {
    try {
      // An error thrown while sending must not loop back into another event.
      if (sending) return;
      const message = scrubMessage(rawMessage);
      // Before the page-view cap, so extension noise never crowds out ours.
      if (isBrowserNoise(message, url, stack)) return;
      if (window.location.pathname !== pagePath) {
        pagePath = window.location.pathname;
        seen = new Set();
        sent = 0;
      }
      if (sent >= MAX_PER_PAGE_VIEW) return;
      const { source, party } = describeSource(url);
      const key = `${kind}|${message}|${source}|${line ?? ""}|${column ?? ""}`;
      if (seen.has(key)) return;
      seen.add(key);
      sent += 1;
      queue.push({
        message,
        source,
        line: line ? String(line) : "",
        column: column ? String(column) : "",
        page_path: pagePath,
        party,
        kind,
        reason: message,
        field: DEEP_STACK.test(message)
          ? stackField(url, line, column, stack)
          : line
            ? `${source}:${line}`
            : source,
        event_category: party,
        event_label: kind,
        error_code: classifyOrigin(message, url),
      });
      flush();
    } catch {
      /* error reporting must never break the page */
    }
  };

  window.addEventListener("error", (event: ErrorEvent) => {
    // Resource load failures (img/script 404s) also fire "error" on window
    // in the capture phase only; this bubble-phase listener sees script
    // errors, which are ErrorEvents with a message.
    if (!(event instanceof ErrorEvent)) return;
    const stack = (event.error as { stack?: unknown } | null | undefined)?.stack;
    report("error", event.message, event.filename, event.lineno, event.colno, stack);
  });

  window.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
    const reason = event.reason as { message?: unknown; name?: unknown; stack?: unknown } | null;
    const message =
      reason && typeof reason === "object" && "message" in reason
        ? `${typeof reason.name === "string" ? reason.name + ": " : ""}${String(reason.message)}`
        : reason;
    const stack = reason && typeof reason === "object" ? reason.stack : undefined;
    const frame = firstFrame(stack);
    report("unhandledrejection", message, frame.url, frame.line, frame.column, stack);
  });

  if (!zarazReady()) {
    const started = Date.now();
    const waiter = window.setInterval(() => {
      if (zarazReady() || Date.now() - started > ZARAZ_WAIT_MS) {
        window.clearInterval(waiter);
        flush();
      }
    }, 500);
  }
}
